import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  effect,
  inject,
  Injector,
  input,
  model,
  WritableSignal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { menuBar, MenuBarButton, MenuBarService } from '@softapps/core';
import { PageComponent, PageHeaderComponent, Panel } from '@softapps/core/pages';
import { handleRequestError } from '@softline/application';
import {
  ButtonComponent,
  IconComponent,
  MessageBarService,
  translator,
} from '@softline/ui-core';
import { ProgressStepsComponent, ProgressStep } from '@softline/ui-core/progress';
import { DsTabsComponent, TabItem } from '@softline/ui-core/tabs';
import { WorkflowDetailsTabComponent } from '../../components/workflow-details-tab/workflow-details-tab.component';
import { WorkflowService } from '../../services/workflow.service';
import { Workflow, WorkflowAction } from '../../types/workflow';
import { WORKFLOW_TAB_VALUE } from '../../workflow.shared';

@Component({
  selector: 'soft-workflow-page',
  imports: [
    PageComponent,
    IconComponent,
    ButtonComponent,
    Panel,
    PageHeaderComponent,
    CommonModule,
    ProgressStepsComponent,
    DsTabsComponent,
    WorkflowDetailsTabComponent,
  ],
  templateUrl: './workflow-page.component.html',
  styleUrls: ['./workflow-page.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowPageComponent {
  private readonly injector = inject(Injector);
  private readonly workflowService = inject(WorkflowService);
  private readonly messageBarService = inject(MessageBarService);

  public readonly workflowId = input.required<number>();

  public readonly workflow: WritableSignal<Workflow | null | undefined> =
    this.workflowService.value;

  private readonly t = translator();

  public readonly header = contentChild(PageHeaderComponent, { descendants: true });
  public readonly saveHandler = input<() => Promise<void> | void>(() =>
    Promise.resolve()
  );

  public readonly tabs = input<readonly TabItem<unknown>[]>([]);
  public readonly activeTab = model<unknown>(null);
  public readonly workflowTabTitle = input<string>('Workflow');

  public readonly mergedTabs = computed<TabItem<unknown>[]>(() => [
    ...this.tabs(),
    { title: this.workflowTabTitle(), value: WORKFLOW_TAB_VALUE },
  ]);
  public readonly isWorkflowTabActive = computed(
    () => this.activeTab() === WORKFLOW_TAB_VALUE
  );

  readonly steps = computed<ProgressStep[]>(() => {
    const workflow = this.workflow();
    if (!workflow) return [];
    const currentActionStep = workflow.currentAction.title;
    const actions = workflow.actions;
    const actionsSteps = actions.map((a) => ({ header: a.title }));
    return [{ header: currentActionStep }, ...actionsSteps];
  });

  constructor() {
    effect(() => {
      this.workflowService.workflowId.set(this.workflowId());
    });
  }

  public readonly createNextAction = async (action: WorkflowAction) => {
    try {
      const updatedWorkflow = await this.workflowService.createNextAction(
        this.workflowId(),
        action
      );
      if (!updatedWorkflow) return;
      this.messageBarService.success(this.t('The action has been created successfully.'));
      await this.workflowService.participantCheck(updatedWorkflow);
    } catch (error) {
      handleRequestError(error, this.injector, { show: true });
    }
  };
}
